import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Bold,
  ImagePlus,
  Italic,
  List,
  ListOrdered,
  Paperclip,
  PenLine,
  X,
  Loader2,
  Check,
} from 'lucide-react';
import {
  fetchEmailReminderMailOptions,
  fetchEmailSignature,
  saveEmailSignature,
  presignEmailSignature,
  putFileToS3,
  sendEmailReminder,
} from '../services/boardChatService';
import { BchTipLayer, useBchTip } from './BchFloatTip';
import './BoardChatEmailReminderPanel.css';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;
const MAX_ATTACHMENTS = 5;
const MAX_IMG = 10 * 1024 * 1024;
const MAX_VIDEO = 50 * 1024 * 1024;
const MAX_PDF = 20 * 1024 * 1024;
const ACCEPT_ATTACH =
  'image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm,video/x-msvideo,video/x-matroska,application/pdf,.pdf,.jpg,.jpeg,.png,.webp,.gif,.mp4,.mov,.webm,.avi,.mkv';

function readUserData() {
  try {
    return JSON.parse(localStorage.getItem('userData') || 'null') || {};
  } catch {
    return {};
  }
}

function userDisplayName() {
  const u = readUserData();
  if (u.name) return String(u.name).trim();
  if (u.username) return String(u.username).trim();
  const email = String(u.email || u.user || '').trim();
  if (email.includes('@')) return email.split('@')[0];
  return email || 'Atendente';
}

function isValidEmail(value) {
  return EMAIL_RE.test(String(value || '').trim());
}

function splitEmails(raw) {
  return String(raw || '')
    .split(/[,;\s]+/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
}

function classifyFile(file) {
  const mime = String(file?.type || '').toLowerCase().split(';')[0].trim();
  const name = String(file?.name || '').toLowerCase();
  const ext = name.includes('.') ? name.split('.').pop() : '';
  if (mime.startsWith('image/') || ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext)) {
    return { kind: 'image', max: MAX_IMG, label: 'imagem' };
  }
  if (mime.startsWith('video/') || ['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(ext)) {
    return { kind: 'video', max: MAX_VIDEO, label: 'vídeo' };
  }
  if (mime === 'application/pdf' || ext === 'pdf') {
    return { kind: 'pdf', max: MAX_PDF, label: 'PDF' };
  }
  return null;
}

function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function execFmt(cmd, value = null) {
  try {
    document.execCommand(cmd, false, value);
  } catch {
    /* ignore */
  }
}

function pickPrimaryFromOption(fromOptions, source) {
  const list = Array.isArray(fromOptions) ? fromOptions : [];
  if (!list.length) return '';
  const bySource = list.find((o) => o.source === source);
  return String((bySource || list[0]).id || '');
}

function fromOptionTitle(opt) {
  const src = String(opt?.source || '').toLowerCase();
  if (src === 'user') return 'Email perfil';
  if (src === 'empresa') return 'Email empresa';
  if (src === 'geral') return 'Email geral';
  return 'Email';
}

function fromOptionEmail(opt) {
  const email = String(opt?.email || '').trim();
  if (email) return email;
  const label = String(opt?.label || '');
  const m = label.match(/[^\s·]+@[^\s·]+/);
  return m ? m[0] : label;
}

function escapeHtmlAttr(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function buildInitialSubject(protocolLabel) {
  const base = `${userDisplayName()} enviou uma mensagem sobre o suporte`;
  const proto = String(protocolLabel || '').trim();
  return proto ? `${base}\n${proto}` : base;
}

function buildPortalBlockHtml(portalUrl) {
  const url = String(portalUrl || '').trim();
  if (!url) return '';
  const href = escapeHtmlAttr(url);
  return (
    `<div data-bch-er-portal="1" contenteditable="false" style="margin-top:18px;">` +
    `<p style="margin:0 0 12px;font-size:14px;line-height:1.55;color:#18181f;">` +
    `Acesse nosso portal para entrar em contato conosco.</p>` +
    `<p style="margin:0;">` +
    `<a href="${href}" target="_blank" rel="noopener noreferrer" ` +
    `style="display:inline-block;padding:9px 16px;border-radius:8px;` +
    `background:#5b3ce8;color:#ffffff;text-decoration:none;font-size:13px;font-weight:650;">` +
    `Acessar portal</a></p></div>`
  );
}

function buildSignatureBlockHtml(bodyHtml, imageUrl) {
  const html = String(bodyHtml || '').trim();
  const img = String(imageUrl || '').trim();
  if (!html && !img) return '';
  let inner = '';
  if (html) inner += html;
  if (img) {
    inner +=
      `<div style="margin-top:8px"><img src="${escapeHtmlAttr(img)}" alt="" ` +
      `style="max-width:280px;height:auto;border:0;"/></div>`;
  }
  return (
    `<div data-bch-er-sig="1" contenteditable="false" style="margin-top:14px;border-top:none;padding-top:0;">` +
    `<div style="color:#9c9ca8;font-size:12px;margin-bottom:6px;">--</div>${inner}</div>`
  );
}

function stripEditorBlocks(html, { portal = false, signature = false } = {}) {
  if (typeof document === 'undefined') return String(html || '');
  const wrap = document.createElement('div');
  wrap.innerHTML = String(html || '');
  if (portal) {
    wrap.querySelectorAll('[data-bch-er-portal]').forEach((el) => el.remove());
  }
  if (signature) {
    wrap.querySelectorAll('[data-bch-er-sig]').forEach((el) => el.remove());
  }
  return wrap.innerHTML;
}

function plainTextFromHtml(html) {
  if (typeof document === 'undefined') {
    return String(html || '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }
  const wrap = document.createElement('div');
  wrap.innerHTML = String(html || '');
  return String(wrap.textContent || '')
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function BoardChatEmailReminderPanel({
  className = '',
  conversationId,
  protocolLabel = '',
  contactEmail = '',
  fromOptions = [],
  primarySource = null,
  portalBaseUrl = '',
  onCancel,
  onSent,
}) {
  const bodyId = useId();
  const sigId = useId();
  const initialEmail = isValidEmail(contactEmail) ? String(contactEmail).trim().toLowerCase() : '';

  const [recipients, setRecipients] = useState(() =>
    initialEmail ? [{ email: initialEmail, kind: 'to' }] : []
  );
  const [toDraft, setToDraft] = useState('');
  const [mailFromOptions, setMailFromOptions] = useState(() =>
    Array.isArray(fromOptions) ? fromOptions : []
  );
  const [mailPrimarySource, setMailPrimarySource] = useState(primarySource || null);
  const [fromId, setFromId] = useState(() =>
    pickPrimaryFromOption(fromOptions, primarySource)
  );
  const [fromLoading, setFromLoading] = useState(
    () => !(Array.isArray(fromOptions) && fromOptions.length)
  );
  const [subject, setSubject] = useState(() => buildInitialSubject(protocolLabel));
  const [files, setFiles] = useState([]);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [sendOk, setSendOk] = useState(false);

  const lastProtocolRef = useRef(protocolLabel);
  const lastConvRef = useRef(conversationId);
  useEffect(() => {
    if (lastConvRef.current !== conversationId || lastProtocolRef.current !== protocolLabel) {
      lastConvRef.current = conversationId;
      lastProtocolRef.current = protocolLabel;
      setSubject(buildInitialSubject(protocolLabel));
    }
  }, [conversationId, protocolLabel]);

  const [ccPopover, setCcPopover] = useState(null); // 'cc' | 'bcc' | null
  const [ccDraft, setCcDraft] = useState('');
  const [ccPopoverPos, setCcPopoverPos] = useState(null);
  const [sigModalOpen, setSigModalOpen] = useState(false);
  const [sigImageUrl, setSigImageUrl] = useState('');
  const [sigSaving, setSigSaving] = useState(false);
  const [sigDirty, setSigDirty] = useState(false);
  const [sigLoaded, setSigLoaded] = useState(false);
  const { tip, tipProps, hideTip } = useBchTip();

  const bodyRef = useRef(null);
  const sigRef = useRef(null);
  const sigHtmlRef = useRef('');
  const bodySeededRef = useRef(false);
  const toInputRef = useRef(null);
  const ccPopoverInputRef = useRef(null);
  const ccPopoverRef = useRef(null);
  const ccBtnRef = useRef(null);
  const bccBtnRef = useRef(null);
  const fileInputRef = useRef(null);
  const sigFileRef = useRef(null);

  const syncBodyFooter = (opts = {}) => {
    if (!bodyRef.current) return;
    const portalUrl = opts.portalUrl != null ? opts.portalUrl : portalBaseUrl;
    const sigHtml = opts.sigHtml != null ? opts.sigHtml : sigHtmlRef.current;
    const imageUrl = opts.imageUrl != null ? opts.imageUrl : sigImageUrl;
    const current = bodyRef.current.innerHTML || '';
    const userHtml = stripEditorBlocks(current, { portal: true, signature: true });
    const hasUser = Boolean(plainTextFromHtml(userHtml));
    const base = hasUser ? userHtml : '<div><br></div>';
    bodyRef.current.innerHTML =
      `${base}${buildPortalBlockHtml(portalUrl)}${buildSignatureBlockHtml(sigHtml, imageUrl)}`;
  };

  useEffect(() => {
    if (!Array.isArray(fromOptions) || !fromOptions.length) return;
    setMailFromOptions(fromOptions);
    setMailPrimarySource(primarySource || null);
    setFromLoading(false);
  }, [fromOptions, primarySource]);

  useEffect(() => {
    setFromId(pickPrimaryFromOption(mailFromOptions, mailPrimarySource));
  }, [mailFromOptions, mailPrimarySource]);

  useEffect(() => {
    if (!conversationId) return undefined;
    if (Array.isArray(fromOptions) && fromOptions.length) return undefined;
    let cancelled = false;
    setFromLoading(true);
    (async () => {
      try {
        const res = await fetchEmailReminderMailOptions(conversationId);
        if (cancelled) return;
        const data = res?.data || res || {};
        const opts = Array.isArray(data.fromOptions) ? data.fromOptions : [];
        setMailFromOptions(opts);
        setMailPrimarySource(data.source || null);
      } catch {
        if (!cancelled) setMailFromOptions([]);
      } finally {
        if (!cancelled) setFromLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [conversationId, fromOptions]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchEmailSignature();
        if (cancelled) return;
        const data = res?.data || res || {};
        setSigImageUrl(data.imageUrl ? String(data.imageUrl) : '');
        sigHtmlRef.current = data.bodyHtml || '';
      } catch {
        if (!cancelled) {
          sigHtmlRef.current = '';
          setSigImageUrl('');
        }
      } finally {
        if (!cancelled) setSigLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!sigLoaded || bodySeededRef.current) return;
    if (!bodyRef.current) return;
    bodySeededRef.current = true;
    syncBodyFooter({
      portalUrl: portalBaseUrl,
      sigHtml: sigHtmlRef.current,
      imageUrl: sigImageUrl,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sigLoaded, portalBaseUrl, sigImageUrl]);

  useEffect(() => {
    if (!bodySeededRef.current || !bodyRef.current) return;
    syncBodyFooter({ portalUrl: portalBaseUrl });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalBaseUrl]);

  useEffect(() => {
    if (!sigModalOpen) return undefined;
    const html = sigHtmlRef.current || '';
    const t = window.setTimeout(() => {
      if (sigRef.current) sigRef.current.innerHTML = html;
    }, 0);
    return () => window.clearTimeout(t);
  }, [sigModalOpen]);

  useEffect(() => {
    if (!ccPopover) {
      setCcPopoverPos(null);
      return undefined;
    }
    const anchor = ccPopover === 'bcc' ? bccBtnRef.current : ccBtnRef.current;
    if (anchor) {
      const r = anchor.getBoundingClientRect();
      const width = 250;
      let left = r.right - width;
      left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
      let top = r.bottom + 8;
      const maxH = 200;
      if (top + maxH > window.innerHeight - 8) {
        top = Math.max(8, r.top - maxH - 8);
      }
      const arrowLeft = Math.min(Math.max(r.left + r.width / 2 - left, 16), width - 16);
      setCcPopoverPos({ top, left, width, arrowLeft });
    }
    const onDoc = (e) => {
      if (ccPopoverRef.current && !ccPopoverRef.current.contains(e.target)) {
        if (ccBtnRef.current?.contains(e.target) || bccBtnRef.current?.contains(e.target)) return;
        setCcPopover(null);
        setCcDraft('');
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setCcPopover(null);
        setCcDraft('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    const t = window.setTimeout(() => ccPopoverInputRef.current?.focus(), 20);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.clearTimeout(t);
    };
  }, [ccPopover]);

  const openCcPopover = (kind) => {
    setCcPopover((p) => (p === kind ? null : kind));
    setCcDraft('');
  };

  const upsertRecipient = (email, kind) => {
    const e = String(email || '').trim().toLowerCase();
    if (!isValidEmail(e)) return false;
    setRecipients((prev) => {
      const idx = prev.findIndex((r) => r.email === e);
      if (idx >= 0) {
        const next = [...prev];
        const cur = next[idx];
        if (cur.kind === 'to' && (kind === 'cc' || kind === 'bcc')) {
          next[idx] = { ...cur, also: kind };
        } else {
          next[idx] = { email: e, kind };
        }
        return next;
      }
      return [...prev, { email: e, kind }];
    });
    return true;
  };

  const removeRecipient = (email) => {
    setRecipients((prev) => prev.filter((r) => r.email !== email));
  };

  const commitToDraft = (raw) => {
    const parts = splitEmails(raw);
    if (!parts.length) return;
    parts.forEach((p) => upsertRecipient(p, 'to'));
    setToDraft('');
  };

  const commitCcPopover = () => {
    const parts = splitEmails(ccDraft);
    if (!parts.length) return;
    const kind = ccPopover === 'bcc' ? 'bcc' : 'cc';
    parts.forEach((p) => upsertRecipient(p, kind));
    setCcDraft('');
    setCcPopover(null);
  };

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    setError('');
    const next = [...files];
    for (const file of incoming) {
      if (next.length >= MAX_ATTACHMENTS) {
        setError(`Máximo de ${MAX_ATTACHMENTS} anexos.`);
        break;
      }
      const kind = classifyFile(file);
      if (!kind) {
        setError('Anexe apenas imagens, vídeos ou PDF.');
        continue;
      }
      if (file.size > kind.max) {
        setError(`${kind.label} acima do limite (${formatBytes(kind.max)}).`);
        continue;
      }
      next.push(file);
    }
    setFiles(next);
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const persistSignature = async ({ clearImage = false, imageS3Key } = {}) => {
    const bodyHtml = sigRef.current?.innerHTML || sigHtmlRef.current || '';
    sigHtmlRef.current = bodyHtml;
    const payload = { bodyHtml };
    if (clearImage) payload.clearImage = true;
    if (imageS3Key) payload.imageS3Key = imageS3Key;
    const res = await saveEmailSignature(payload);
    const data = res?.data || res || {};
    const nextImage = data.imageUrl ? String(data.imageUrl) : '';
    setSigImageUrl(nextImage);
    setSigDirty(false);
    syncBodyFooter({
      sigHtml: sigHtmlRef.current,
      imageUrl: nextImage,
    });
    return data;
  };

  const onSigImagePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const mime = String(file.type || '').toLowerCase();
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(mime)) {
      setError('Assinatura: use JPEG, PNG ou WebP.');
      return;
    }
    if (file.size > MAX_IMG) {
      setError(`Imagem da assinatura acima de ${formatBytes(MAX_IMG)}.`);
      return;
    }
    setSigSaving(true);
    setError('');
    try {
      const pre = await presignEmailSignature({
        fileName: file.name,
        contentType: mime,
      });
      const data = pre?.data || pre || {};
      if (!data.uploadUrl || !data.key) throw new Error('Falha ao preparar upload.');
      await putFileToS3(data.uploadUrl, file, mime);
      await persistSignature({ imageS3Key: data.key });
    } catch (err) {
      setError(err.message || 'Falha ao enviar imagem da assinatura.');
    } finally {
      setSigSaving(false);
    }
  };

  const clearSigImage = async () => {
    setSigSaving(true);
    setError('');
    try {
      await persistSignature({ clearImage: true });
    } catch (err) {
      setError(err.message || 'Falha ao remover imagem.');
    } finally {
      setSigSaving(false);
    }
  };

  const saveSigText = async () => {
    if (sigRef.current) sigHtmlRef.current = sigRef.current.innerHTML || '';
    if (!sigDirty) return;
    setSigSaving(true);
    setError('');
    try {
      await persistSignature();
    } catch (err) {
      setError(err.message || 'Falha ao salvar assinatura.');
    } finally {
      setSigSaving(false);
    }
  };

  const selectedFrom =
    (mailFromOptions || []).find((o) => String(o.id) === String(fromId)) ||
    (mailFromOptions || [])[0] ||
    null;

  const handleSend = async () => {
    if (sending || sendOk) return;
    let list = [...recipients];
    if (toDraft.trim() && isValidEmail(toDraft)) {
      const e = toDraft.trim().toLowerCase();
      if (!list.some((r) => r.email === e)) list = [...list, { email: e, kind: 'to' }];
    }
    const toList = list.filter((r) => r.kind === 'to').map((r) => r.email);
    const ccList = [
      ...list.filter((r) => r.kind === 'cc').map((r) => r.email),
      ...list.filter((r) => r.kind === 'to' && r.also === 'cc').map((r) => r.email),
    ];
    const bccList = [
      ...list.filter((r) => r.kind === 'bcc').map((r) => r.email),
      ...list.filter((r) => r.kind === 'to' && r.also === 'bcc').map((r) => r.email),
    ];
    if (!toList.length && !ccList.length && !bccList.length) {
      setError('Informe ao menos um destinatário.');
      return;
    }
    if (!toList.length) {
      setError('Informe ao menos um destinatário em Para.');
      return;
    }
    if (!String(subject || '').trim()) {
      setError('Informe o assunto.');
      return;
    }
    if (!selectedFrom) {
      setError('Selecione o remetente (De).');
      return;
    }
    if (String(fromId) !== String(selectedFrom.id)) {
      setFromId(String(selectedFrom.id));
    }
    const rawBody = bodyRef.current?.innerHTML || '';
    const bodyForSend = stripEditorBlocks(rawBody, { signature: true });
    const checkText = plainTextFromHtml(bodyForSend);
    if (!checkText) {
      setError('Escreva a mensagem.');
      return;
    }
    setSending(true);
    setSendOk(false);
    setError('');
    try {
      if (sigDirty) {
        try {
          await persistSignature();
        } catch {
          /* send even if sig save fails */
        }
      }
      const json = await sendEmailReminder(conversationId, {
        to: toList,
        cc: ccList,
        bcc: bccList,
        subject: String(subject || '')
          .replace(/\r\n/g, '\n')
          .replace(/\n+/g, ' ')
          .replace(/\s+/g, ' ')
          .trim(),
        bodyHtml: bodyForSend,
        files,
        mailSource: selectedFrom.source,
        empresaCodigo:
          selectedFrom.source === 'empresa' ? selectedFrom.empresaCodigo : undefined,
      });
      const count = Number(json?.data?.count ?? json?.count) || 0;
      setSendOk(true);
      setSending(false);
      window.setTimeout(() => {
        onSent?.(count);
        onCancel?.();
      }, 1100);
    } catch (err) {
      const apiMsg = String(err?.message || '').trim();
      setError(apiMsg || 'Falha ao enviar e-mail.');
      setSending(false);
      setSendOk(false);
    }
  };

  const onToKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === 'Tab' || e.key === ',' || e.key === ';') {
      if (toDraft.trim()) {
        e.preventDefault();
        commitToDraft(toDraft);
      }
      return;
    }
    if (e.key === 'Backspace' && !toDraft && recipients.length) {
      removeRecipient(recipients[recipients.length - 1].email);
    }
  };

  const signatureModal =
    sigModalOpen &&
    createPortal(
      <div
        className="bch-er-sig-overlay"
        role="presentation"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) {
            saveSigText();
            setSigModalOpen(false);
          }
        }}
      >
        <div
          className="bch-er-sig-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Assinatura"
        >
          <div className="bch-er-sig-modal-head">
            <h3 className="bch-er-sig-modal-title">Assinatura</h3>
            <button
              type="button"
              className="bch-er-iconbtn"
              aria-label="Fechar"
              {...tipProps('Fechar', 'below')}
              onClick={() => {
                hideTip();
                saveSigText();
                setSigModalOpen(false);
              }}
            >
              <X size={14} strokeWidth={1.8} />
            </button>
          </div>
          <div className="bch-er-sig-modal-body">
            <div
              id={sigId}
              ref={sigRef}
              className="bch-er-sig-editor"
              contentEditable
              role="textbox"
              aria-multiline="true"
              aria-label="Texto da assinatura"
              data-placeholder="Sua assinatura…"
              suppressContentEditableWarning
              onInput={() => {
                setSigDirty(true);
                if (sigRef.current) sigHtmlRef.current = sigRef.current.innerHTML || '';
              }}
            />
          </div>
          <div className="bch-er-sig-modal-footer">
            <div className="bch-er-sig-media">
              {sigImageUrl ? (
                <div className="bch-er-sig-preview">
                  <img src={sigImageUrl} alt="Assinatura" />
                  <button
                    type="button"
                    className="bch-er-link"
                    disabled={sigSaving}
                    onClick={clearSigImage}
                  >
                    Remover imagem
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="bch-er-attach-btn"
                  disabled={sigSaving}
                  onClick={() => sigFileRef.current?.click()}
                >
                  <ImagePlus size={15} strokeWidth={1.9} />
                  Imagem
                </button>
              )}
              <input
                ref={sigFileRef}
                type="file"
                hidden
                accept="image/jpeg,image/png,image/webp"
                onChange={onSigImagePick}
              />
              {sigSaving ? <span className="bch-er-sig-status">Salvando…</span> : null}
            </div>
            <button
              type="button"
              className="bch-er-sig-save"
              disabled={sigSaving}
              onClick={async () => {
                await saveSigText();
                setSigModalOpen(false);
              }}
            >
              Salvar
            </button>
          </div>
        </div>
      </div>,
      document.body
    );

  return (
    <section className={`bch-card bch-card--email-reminder ${className}`.trim()}>
      <div className="bch-er-header">
        <h2 className="bch-er-title">Nova mensagem</h2>
        <button
          type="button"
          className="bch-er-iconbtn"
          onClick={() => {
            hideTip();
            onCancel?.();
          }}
          aria-label="Fechar"
          {...tipProps('Fechar', 'below')}
        >
          <X size={14} strokeWidth={1.8} />
        </button>
      </div>

      <div className="bch-er-body">
        <div className="bch-er-to-row bch-er-from-row">
          <span className="bch-er-label">De</span>
          {fromLoading && !(mailFromOptions || []).length ? (
            <span className="bch-er-from-placeholder">Carregando remetentes…</span>
          ) : (mailFromOptions || []).length > 0 ? (
            <div className="bch-er-chips bch-er-from-chips" role="radiogroup" aria-label="Remetente">
              {(mailFromOptions || []).map((opt) => {
                const id = String(opt.id);
                const selected =
                  fromId && (mailFromOptions || []).some((o) => String(o.id) === String(fromId))
                    ? String(fromId) === id
                    : String((mailFromOptions || [])[0]?.id || '') === id;
                const emailTip = fromOptionEmail(opt);
                return (
                  <button
                    key={id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    className={`bch-er-chip bch-er-from-chip${selected ? ' is-selected' : ''}`}
                    onClick={() => setFromId(id)}
                    {...(emailTip ? tipProps(emailTip, 'below') : {})}
                  >
                    <span className="bch-er-chip-text">{fromOptionTitle(opt)}</span>
                  </button>
                );
              })}
            </div>
          ) : (
            <span className="bch-er-from-placeholder">Nenhum remetente configurado</span>
          )}
        </div>

        <div className="bch-er-to-block">
          <div className="bch-er-to-row">
            <span className="bch-er-label">Para</span>
            <div
              className="bch-er-chips"
              onClick={() => toInputRef.current?.focus()}
              role="group"
              aria-label="Para"
            >
              {recipients.map((r) => {
                const badge =
                  r.kind === 'cc' || r.also === 'cc'
                    ? 'Cc'
                    : r.kind === 'bcc' || r.also === 'bcc'
                      ? 'Cco'
                      : null;
                return (
                  <span key={r.email} className="bch-er-chip">
                    <span className="bch-er-chip-text">{r.email}</span>
                    {badge ? <span className="bch-er-chip-badge">{badge}</span> : null}
                    <button
                      type="button"
                      className="bch-er-chip-x"
                      aria-label={`Remover ${r.email}`}
                      {...tipProps(`Remover ${r.email}`, 'below')}
                      onClick={(ev) => {
                        ev.stopPropagation();
                        hideTip();
                        removeRecipient(r.email);
                      }}
                    >
                      <X size={9} strokeWidth={1.6} />
                    </button>
                  </span>
                );
              })}
              <input
                ref={toInputRef}
                type="text"
                className="bch-er-add-input"
                value={toDraft}
                onChange={(e) => setToDraft(e.target.value)}
                onKeyDown={onToKeyDown}
                onPaste={(e) => {
                  const text = e.clipboardData?.getData('text') || '';
                  if (!text || !/[,;\s]/.test(text)) return;
                  e.preventDefault();
                  commitToDraft(`${toDraft} ${text}`);
                }}
                onBlur={() => {
                  if (toDraft.trim()) commitToDraft(toDraft);
                }}
                placeholder="Adicionar destinatário…"
                autoComplete="off"
                spellCheck={false}
                aria-label="Destinatários"
              />
            </div>
            <div className="bch-er-cc-toggles">
              <button
                ref={ccBtnRef}
                type="button"
                className={`bch-er-cc-toggle${ccPopover === 'cc' ? ' is-active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  hideTip();
                  openCcPopover('cc');
                }}
                {...tipProps('Cópia (Cc)', 'below')}
              >
                Cc
              </button>
              <button
                ref={bccBtnRef}
                type="button"
                className={`bch-er-cc-toggle${ccPopover === 'bcc' ? ' is-active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  hideTip();
                  openCcPopover('bcc');
                }}
                {...tipProps('Cópia oculta (Cco)', 'below')}
              >
                Cco
              </button>
            </div>
          </div>
        </div>

        <div className="bch-er-context">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
            <rect x="2" y="3" width="12" height="10" rx="2" stroke="currentColor" strokeWidth="1.3" />
            <path
              d="M2.5 4L8 9L13.5 4"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
            />
          </svg>
          <textarea
            className="bch-er-context-input"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={500}
            rows={2}
            aria-label="Assunto"
            placeholder="Assunto"
          />
        </div>

        <div
          id={bodyId}
          ref={bodyRef}
          className="bch-er-editor"
          contentEditable
          role="textbox"
          aria-multiline="true"
          aria-label="Corpo da mensagem"
          data-placeholder="Escreva sua mensagem…"
          suppressContentEditableWarning
        />

        {files.length ? (
          <ul className="bch-er-file-list">
            {files.map((f, i) => (
              <li key={`${f.name}-${f.size}-${i}`} className="bch-er-file-chip">
                <span className="bch-er-file-name" {...tipProps(f.name, 'above')}>
                  {f.name}
                </span>
                <span className="bch-er-file-size">{formatBytes(f.size)}</span>
                <button
                  type="button"
                  className="bch-er-chip-x"
                  aria-label={`Remover ${f.name}`}
                  {...tipProps(`Remover ${f.name}`, 'above')}
                  onClick={() => {
                    hideTip();
                    removeFile(i);
                  }}
                >
                  <X size={9} strokeWidth={1.6} />
                </button>
              </li>
            ))}
          </ul>
        ) : null}

      </div>

      <div className="bch-er-toolbar" role="toolbar" aria-label="Formatação">
        <button
          type="button"
          className="bch-er-tbtn"
          aria-label="Negrito"
          {...tipProps('Negrito', 'above')}
          onMouseDown={(e) => {
            e.preventDefault();
            execFmt('bold');
          }}
        >
          <Bold size={13} strokeWidth={2.3} />
        </button>
        <button
          type="button"
          className="bch-er-tbtn"
          aria-label="Itálico"
          {...tipProps('Itálico', 'above')}
          onMouseDown={(e) => {
            e.preventDefault();
            execFmt('italic');
          }}
        >
          <Italic size={13} strokeWidth={2.3} />
        </button>
        <span className="bch-er-sep" />
        <button
          type="button"
          className="bch-er-tbtn bch-er-tbtn--text"
          aria-label="Título 1"
          {...tipProps('Título 1', 'above')}
          onMouseDown={(e) => {
            e.preventDefault();
            execFmt('formatBlock', 'h1');
          }}
        >
          H1
        </button>
        <button
          type="button"
          className="bch-er-tbtn bch-er-tbtn--text"
          aria-label="Título 2"
          {...tipProps('Título 2', 'above')}
          onMouseDown={(e) => {
            e.preventDefault();
            execFmt('formatBlock', 'h2');
          }}
        >
          H2
        </button>
        <span className="bch-er-sep" />
        <button
          type="button"
          className="bch-er-tbtn"
          aria-label="Lista"
          {...tipProps('Lista', 'above')}
          onMouseDown={(e) => {
            e.preventDefault();
            execFmt('insertUnorderedList');
          }}
        >
          <List size={13} strokeWidth={1.9} />
        </button>
        <button
          type="button"
          className="bch-er-tbtn"
          aria-label="Lista numerada"
          {...tipProps('Lista numerada', 'above')}
          onMouseDown={(e) => {
            e.preventDefault();
            execFmt('insertOrderedList');
          }}
        >
          <ListOrdered size={13} strokeWidth={1.9} />
        </button>
        <span className="bch-er-sep" />
        <button
          type="button"
          className="bch-er-tbtn"
          aria-label="Anexar"
          {...tipProps('Anexar arquivo', 'above')}
          onMouseDown={(e) => {
            e.preventDefault();
            fileInputRef.current?.click();
          }}
          disabled={files.length >= MAX_ATTACHMENTS}
        >
          <Paperclip size={13} strokeWidth={1.9} />
        </button>
        <button
          type="button"
          className="bch-er-tbtn"
          aria-label="Assinatura"
          {...tipProps('Assinatura', 'above')}
          onMouseDown={(e) => {
            e.preventDefault();
            hideTip();
            setSigModalOpen(true);
          }}
        >
          <PenLine size={13} strokeWidth={1.9} />
        </button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        hidden
        multiple
        accept={ACCEPT_ATTACH}
        onChange={(e) => {
          addFiles(e.target.files);
          e.target.value = '';
        }}
      />

      <div className="bch-er-footer">
        <div className="bch-er-footer-status">
          {error ? (
            <p className="bch-er-error" role="alert">
              {error}
            </p>
          ) : null}
          {sendOk ? (
            <p className="bch-er-success" role="status">
              E-mail enviado com sucesso.
            </p>
          ) : null}
          {sending && !error && !sendOk ? (
            <p className="bch-er-sending-hint" role="status">
              Processando envio…
            </p>
          ) : null}
        </div>
        <button
          type="button"
          className={`bch-er-send${sendOk ? ' is-ok' : ''}${sending ? ' is-busy' : ''}`}
          disabled={sending || sendOk || !conversationId}
          onClick={handleSend}
          aria-busy={sending || undefined}
        >
          {sending ? (
            <Loader2 size={14} strokeWidth={2.2} className="bch-er-send-spin" aria-hidden />
          ) : sendOk ? (
            <Check size={14} strokeWidth={2.4} aria-hidden />
          ) : (
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
              <path
                d="M12.8 1.2L1.2 6.4L5.6 8.3L7.5 12.7L12.8 1.2Z"
                stroke="white"
                strokeWidth="1.2"
                strokeLinejoin="round"
              />
              <path
                d="M5.6 8.3L12.8 1.2"
                stroke="white"
                strokeWidth="1.2"
                strokeLinecap="round"
              />
            </svg>
          )}
          {sending ? 'Enviando…' : sendOk ? 'Enviado' : 'Enviar'}
        </button>
      </div>

      {signatureModal}
      {ccPopover && ccPopoverPos
        ? createPortal(
            <div
              ref={ccPopoverRef}
              className="bch-er-cc-pop"
              role="dialog"
              aria-modal="true"
              aria-label={ccPopover === 'bcc' ? 'Adicionar Cco' : 'Adicionar Cc'}
              style={{
                position: 'fixed',
                top: ccPopoverPos.top,
                left: ccPopoverPos.left,
                width: ccPopoverPos.width,
                zIndex: 12000,
                ['--bch-er-arrow-left']: `${ccPopoverPos.arrowLeft}px`,
              }}
            >
              <div className="bch-er-cc-pop-arrow" aria-hidden />
              <div className="bch-er-cc-pop-shell">
                <div className="bch-er-cc-pop-head">
                  <h4 className="bch-er-cc-pop-title">
                    {ccPopover === 'bcc' ? 'Cópia oculta (Cco)' : 'Cópia (Cc)'}
                  </h4>
                  <p className="bch-er-cc-pop-sub">
                    {ccPopover === 'bcc'
                      ? 'Adicione destinatários em cópia oculta.'
                      : 'Adicione destinatários em cópia.'}
                  </p>
                </div>
                <div className="bch-er-cc-pop-body">
                  <label className="bch-er-cc-pop-label" htmlFor="bch-er-cc-input">
                    E-mail
                  </label>
                  <input
                    id="bch-er-cc-input"
                    ref={ccPopoverInputRef}
                    type="text"
                    className="bch-er-cc-pop-input"
                    placeholder="email@dominio.com"
                    value={ccDraft}
                    onChange={(e) => setCcDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        commitCcPopover();
                      }
                    }}
                  />
                </div>
                <div className="bch-er-cc-pop-foot">
                  <button
                    type="button"
                    className="user-form-link-btn user-form-footer-btn"
                    onClick={() => {
                      setCcPopover(null);
                      setCcDraft('');
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="user-form-profile-save-btn user-form-footer-btn"
                    onClick={commitCcPopover}
                    disabled={!ccDraft.trim()}
                  >
                    Confirmar
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
      <BchTipLayer tip={tip} />
    </section>
  );
}
